import { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/feature/AppShell";
import MemberAvatar from "@/components/feature/MemberAvatar";
import { supabase } from "@/lib/supabaseClient";
import {
  challengeStatus,
  fetchChallenges,
  fetchLeaderboard,
  joinChallenge,
  leaveChallenge,
  METRIC_LABELS,
  METRIC_UNITS,
  type Challenge,
  type ChallengeStatus,
  type LeaderboardEntry,
} from "@/services/challenges";

const STATUS_LABEL: Record<ChallengeStatus, string> = {
  active: "En curso",
  upcoming: "Próximo",
  finished: "Finalizado",
};

const STATUS_STYLE: Record<ChallengeStatus, string> = {
  active: "bg-primary-500/15 text-primary-700",
  upcoming: "bg-secondary-500/15 text-secondary-700",
  finished: "bg-background-200 text-foreground-600",
};

function formatDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("es", { day: "2-digit", month: "short" });
}

export default function Retos() {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [boards, setBoards] = useState<Record<string, LeaderboardEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meId, setMeId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const showNotice = (msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice(null), 3000);
  };

  const loadBoard = useCallback(async (id: string) => {
    try {
      const board = await fetchLeaderboard(id);
      setBoards((prev) => ({ ...prev, [id]: board }));
    } catch {
      /* el ranking es informativo: si falla, la tarjeta sigue funcionando */
    }
  }, []);

  const load = useCallback(async () => {
    const [{ data: userData }, list] = await Promise.all([supabase.auth.getUser(), fetchChallenges()]);
    setMeId(userData.user?.id ?? null);
    const visible = list.filter((c) => c.active);
    setChallenges(visible);
    // El ranking de los retos en los que participo (para mostrar mi progreso real).
    await Promise.all(visible.filter((c) => c.joined).map((c) => loadBoard(c.id)));
  }, [loadBoard]);

  useEffect(() => {
    let active = true;
    load()
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : "No se pudieron cargar los retos.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [load]);

  const toggleJoin = async (c: Challenge) => {
    setBusyId(c.id);
    try {
      if (c.joined) await leaveChallenge(c.id);
      else await joinChallenge(c.id);
      await load();
      if (!c.joined) await loadBoard(c.id);
    } catch {
      showNotice("No se pudo actualizar tu participación.");
    } finally {
      setBusyId(null);
    }
  };

  const toggleExpand = async (c: Challenge) => {
    const next = expanded === c.id ? null : c.id;
    setExpanded(next);
    if (next && !boards[c.id]) await loadBoard(c.id);
  };

  const groups = useMemo(() => {
    const by: Record<ChallengeStatus, Challenge[]> = { active: [], upcoming: [], finished: [] };
    challenges.forEach((c) => by[challengeStatus(c)].push(c));
    return by;
  }, [challenges]);

  const renderCard = (c: Challenge) => {
    const status = challengeStatus(c);
    const board = boards[c.id];
    const mine = board?.find((e) => e.userId === meId);
    const myProgress = mine?.progress ?? 0;
    const pct = Math.min(100, Math.round((myProgress / c.target) * 100));
    const done = myProgress >= c.target;
    return (
      <article key={c.id} className="bg-background-100 border border-background-200 rounded-2xl p-5">
        {c.imageUrl ? (
          <img src={c.imageUrl} alt="" loading="lazy" className="mb-4 w-full rounded-xl object-cover" />
        ) : null}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-foreground-950">{c.title}</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {formatDay(c.startsOn)} – {formatDay(c.endsOn)} · {c.participants}{" "}
              {c.participants === 1 ? "participante" : "participantes"}
            </p>
          </div>
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_STYLE[status]}`}>
            {STATUS_LABEL[status]}
          </span>
        </div>

        {c.description ? <p className="text-sm text-foreground-700 mt-3">{c.description}</p> : null}

        <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-foreground-700 bg-background-50 border border-background-200 rounded-full px-3 py-1.5">
          <i className="ri-flag-2-line text-primary-600"></i>
          Meta: {c.target} {METRIC_UNITS[c.metric]} · {METRIC_LABELS[c.metric]}
        </div>

        {c.joined ? (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-semibold mb-1.5">
              <span className="text-foreground-700">Tu progreso</span>
              <span className={done ? "text-primary-700" : "text-foreground-700"}>
                {myProgress}/{c.target} {done ? "· ¡Meta cumplida! 🎉" : ""}
              </span>
            </div>
            <div className="h-2.5 bg-background-200 rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
            </div>
          </div>
        ) : null}

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {status !== "finished" ? (
            <button
              onClick={() => toggleJoin(c)}
              disabled={busyId === c.id}
              className={[
                "px-4 py-2 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50",
                c.joined
                  ? "border border-background-300 text-foreground-700 hover:border-accent-400"
                  : "bg-primary-500 text-background-950 hover:bg-primary-600",
              ].join(" ")}
            >
              {busyId === c.id ? "Un momento…" : c.joined ? "Salir del reto" : "Unirme al reto"}
            </button>
          ) : null}
          <button
            onClick={() => toggleExpand(c)}
            className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-700 hover:bg-background-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5"
          >
            <i className="ri-bar-chart-horizontal-line"></i> Ranking
            <i className={`ri-arrow-down-s-line transition-transform ${expanded === c.id ? "rotate-180" : ""}`}></i>
          </button>
        </div>

        {expanded === c.id ? (
          <div className="mt-3 pt-3 border-t border-background-200">
            {!board ? (
              <p className="text-sm text-foreground-500">Cargando ranking…</p>
            ) : board.length === 0 ? (
              <p className="text-sm text-foreground-500">Nadie se ha unido todavía. ¡Sé la primera persona!</p>
            ) : (
              <ol className="flex flex-col gap-2">
                {board.map((e, i) => (
                  <li
                    key={e.userId}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2 ${e.userId === meId ? "bg-primary-500/10 border border-primary-500/25" : "bg-background-50 border border-background-200"}`}
                  >
                    <span className="w-6 text-center font-heading font-bold text-sm text-foreground-600">
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                    </span>
                    <MemberAvatar name={e.fullName} src={e.avatarUrl} size="sm" />
                    <span className="flex-1 min-w-0 truncate text-sm font-semibold text-foreground-950">
                      {e.fullName}
                      {e.userId === meId ? <span className="ml-1.5 text-[10px] bg-background-200 text-foreground-600 px-2 py-0.5 rounded-full">Tú</span> : null}
                    </span>
                    <span className="text-sm font-bold text-foreground-800 whitespace-nowrap">
                      {e.progress}/{c.target}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        ) : null}
      </article>
    );
  };

  const section = (title: string, list: Challenge[]) =>
    list.length ? (
      <section className="mt-6">
        <h2 className="font-heading font-bold text-foreground-950 text-base mb-3">{title}</h2>
        <div className="flex flex-col gap-4">{list.map(renderCard)}</div>
      </section>
    ) : null;

  return (
    <AppShell headerVariant="onLight">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-accent-500/15 flex items-center justify-center">
            <i className="ri-trophy-fill text-2xl text-accent-600"></i>
          </div>
          <div>
            <h1 className="font-heading font-bold text-foreground-950 text-xl">Retos</h1>
            <p className="text-xs text-foreground-500">Tu progreso se calcula solo con tu actividad real en Vívelo.</p>
          </div>
        </div>

        {notice ? (
          <div role="alert" className="mt-4 bg-accent-500/10 border border-accent-500/30 text-accent-700 text-sm rounded-xl px-4 py-3">
            {notice}
          </div>
        ) : null}

        {loading ? (
          <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-2xl"></i>
            <p className="mt-2 text-sm">Cargando retos…</p>
          </div>
        ) : error ? (
          <div className="mt-6 bg-background-100 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700 text-sm">{error}</div>
        ) : challenges.length === 0 ? (
          <div className="mt-6 bg-background-100 border border-dashed border-background-300 rounded-2xl p-10 text-center text-foreground-500 text-sm">
            Todavía no hay retos. Tu administrador los publicará pronto.
          </div>
        ) : (
          <>
            {section("En curso", groups.active)}
            {section("Próximos", groups.upcoming)}
            {section("Finalizados", groups.finished)}
          </>
        )}
      </div>
    </AppShell>
  );
}
